import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import * as day from 'dayjs';
import { FlexTimeActivity, IMembershipList, MembershipListType, MingaRoleType } from 'libs/domain';
import { gateway } from 'libs/generated-grpc-web';
import { cloneDeep, isEmpty } from 'lodash';
import { BehaviorSubject, map, ReplaySubject } from 'rxjs';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { TableSort } from '@app/src/app/modules/people-selector';
import { Person } from '@app/src/app/people';
import { ListMembershipService } from '@app/src/app/services/ListMembership';
import { MembershipListTableLists } from '@app/src/app/shared/components/membership-list-table';
import { scrollIntoView } from '@app/src/app/util/scroll-into-view';

import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog';
import { markNestedFormGroupAsDirty } from '@shared/components/crud-form-base/crud-form-base.utils';
import { FormSelectOption } from '@shared/components/form';
import {
  FormRestrictionValue,
  fromRestrictionInput,
  toRestrictionInput,
} from '@shared/components/form-restriction-input/form-restriction-input.constants';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayPrimaryHeaderBackground,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { FlexTimeActivityInstanceService } from '@shared/services/flex-time';
import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';

import {
  FTM_AT_FORM,
  FtmActivityTemplatesEditFormFields,
  FtmActivityTemplatesMessages,
} from '../../ftm-activity-templates.constants';
import { FlexEditModalData, FlexEditModalResponse } from '../../ftm-activity-templates.types';
import { FtmActivityTemplatesService } from '../../services';

const SUBMITTABLE_STATES: FormStateMachine[] = ['idle', 'invalid'];

type FormStateMachine = 'idle' | 'invalid' | 'submitting' | 'creating' | 'updating' | 'deleting' | 'uploading';
@Component({
  selector: 'mg-ftm-at-edit',
  templateUrl: './ftm-at-edit.component.html',
  styleUrls: ['./ftm-at-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmAtEditComponent implements OnDestroy, OnInit {
  private _formState = new BehaviorSubject<FormStateMachine>('idle');
  public formState$ = this._formState.asObservable();
  private _destroyed = new ReplaySubject<void>(1);
  private _isNew = new BehaviorSubject(false);
  public isNew$ = this._isNew.asObservable();
  private _canDelete = new BehaviorSubject(false);
  public canDelete$ = this._canDelete.asObservable();

  @ViewChild('formElement', { static: false })
  formElement?: ElementRef<HTMLElement>;

  public readonly MODAL_CONFIG = {
    headerBg: ModalOverlayPrimaryHeaderBackground.ALT_TEAL,
  };
  public readonly MESSAGES = FtmActivityTemplatesMessages;
  public readonly FORM_FIELDS = FtmActivityTemplatesEditFormFields;
  public readonly excludedRoles = [
    MingaRoleType.STUDENT,
    MingaRoleType.STUDENT_LEADER,
    MingaRoleType.READ_ONLY,
    MingaRoleType.PARENT,
  ];

  /** Form Controls */
  public readonly form = this._fb.group(cloneDeep(FTM_AT_FORM));

  public readonly metadata$ = this.form.get(FtmActivityTemplatesEditFormFields.TEACHER).valueChanges.pipe(
    map(value => {
      if (!value?.hash) return {};

      return {
        [value.hash]: { teacherType: 'Main', disabled: true },
      };
    }),
  );

  private _page = 1;
  private _itemsPerPage = 20;
  // Keep track of 2 seperate lists of images for pseudo lazy loading
  // We've fetched all the data but at least this way we lazy load images
  private _bannersData: gateway.banner_pb.Banner.AsObject[] = [];
  private _imageList = new BehaviorSubject<gateway.banner_pb.Banner.AsObject[]>(null);
  public imageList$ = this._imageList.asObservable();
  public roles: string[] = [MingaRoleType.TEACHER, MingaRoleType.STAFF, MingaRoleType.MANAGER, MingaRoleType.OWNER];
  private _activityTypeOptionsSubj = new BehaviorSubject<FormSelectOption<number>[]>(undefined);
  public activityTypeOptions$ = this._activityTypeOptionsSubj.asObservable();

  /** Membership Details */
  public readonly membershipType = MembershipListType.FLEX_RESTRICTION_LIST;
  public readonly DEFAULT_SORT: TableSort = {
    column: 'teacherType',
    direction: 'desc',
  };

  private _listSubject = new BehaviorSubject<MembershipListTableLists[]>([
    { type: MembershipListType.FLEX_TIME_TEACHER_LIST },
  ]);
  public lists$ = this._listSubject.asObservable();

  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    private _data: FlexEditModalData,
    private _modalOverlay: ModalOverlayRef<FlexEditModalResponse, FlexEditModalData>,
    private _dialog: MatDialog,
    private _fb: UntypedFormBuilder,
    private _ftmAtService: FtmActivityTemplatesService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _sentry: SentryService,
    private _flexPermissions: FlexTimePermissionsService,
    private _flexInstancesService: FlexTimeActivityInstanceService,
    private _cdr: ChangeDetectorRef,
    private _listMembership: ListMembershipService,
    private _authInfo: AuthInfoService,
  ) {
    this._getBanners();
    this._getActivityTypes();

    if (this._flexPermissions.isFlexTimeAdmin()) {
      this.form.get(FtmActivityTemplatesEditFormFields.TEACHER).setValidators(Validators.required);
    }
  }

  async ngOnInit() {
    await this._setInitialForm();
  }

  public get canSubmit() {
    return SUBMITTABLE_STATES.includes(this._formState.value);
  }

  public get submitLabel() {
    const state = this._formState.getValue();
    const isNew = this._isNew.getValue();

    switch (state) {
      case 'creating':
        return FtmActivityTemplatesMessages.MODAL_CREATING_BTN;
      case 'updating':
        return FtmActivityTemplatesMessages.MODAL_UPDATING_BTN;
      case 'uploading':
        return FtmActivityTemplatesMessages.MODAL_UPLOADING_BTN;
      default:
        return isNew ? FtmActivityTemplatesMessages.MODAL_CREATE_BTN : FtmActivityTemplatesMessages.MODAL_UPDATE_BTN;
    }
  }

  public async submit() {
    if (!this.canSubmit) return;
    this._formState.next('submitting');

    markNestedFormGroupAsDirty(this.form);

    if (!this.form.valid) {
      this._formState.next('invalid');
      scrollIntoView(this.formElement.nativeElement, {
        align: { top: 0 },
      });
      return;
    }

    const { banner, allowStudentToRegister, restriction, typeId, teacherListId, ...restFormValues } = this.form.value;

    const activity: FlexTimeActivity = {
      ...this._data,
      ...restFormValues,
      bannerHash: banner.bannerHash || null,
      imagePath: banner.assetPath || null,
      allowStudentToRegister: !!allowStudentToRegister,
      restriction: fromRestrictionInput(restriction, (this._data as any)?.restriction),
      type: {
        id: typeId,
      },
      teacherListId: teacherListId,
    };

    if (this._isNew.getValue()) {
      await this._create({ ...activity, id: undefined });
    } else {
      await this._update(activity);
    }

    this._formState.next('idle');
  }

  public async handleCloseModal() {
    if (this._isNew.getValue()) {
      //delete membershipLists so we don't create orphans
      const teacherListId = this._data.teacherListId;
      if (teacherListId) this._listMembership.deleteMembershipList(teacherListId);
      const restrictions = this.form.get(FtmActivityTemplatesEditFormFields.RESTRICTION_MEMBERSHIP_LIST)
        ?.value as FormRestrictionValue;
      if (restrictions && restrictions.membershipList) {
        this._listMembership.deleteMembershipList(restrictions.membershipList.id);
      }
    }
  }

  public onMembershipListChange(list: IMembershipList) {
    if (list?.id && list.listType === MembershipListType.FLEX_TIME_TEACHER_LIST) {
      this.form.controls[this.FORM_FIELDS.TEACHER_LIST_ID].setValue(list.id);
    }
  }

  public async delete() {
    const isNew = this._isNew.getValue();
    if (isNew) {
      await this.handleCloseModal();
      return this._modalOverlay.close(ModalOverlayServiceCloseEventType.CLOSE);
    }

    await this._canActivityBeDeleted();
    const canDelete = this._canDelete.getValue();

    if (!canDelete) {
      return this._dialog.open(ConfirmationDialogComponent, {
        data: {
          text: {
            description: this.MESSAGES.DIALOG_CONFIRM_UNABLE_TO_DELETE,
            deleteBtn: this.MESSAGES.DIALOG_CONFIRM_UNABLE_TO_DELETE_BTN,
          },
        },
      });
    }
    // if it's a new activity and the user hasn't touched the form we
    // dont need a confirm exit dialog
    if (this.form.pristine && isNew) {
      return this._modalOverlay.close(ModalOverlayServiceCloseEventType.CLOSE);
    }

    const confirmationDialog = this._dialog.open(ConfirmationDialogComponent, {
      data: {
        text: {
          description: this.MESSAGES.DIALOG_CONFIRM_DELETE,
          deleteBtn: this.MESSAGES.DIALOG_CONFIRM_DELETE_BTN,
        },
      },
    });

    confirmationDialog.afterClosed().subscribe(async response => {
      if (!response || !response.confirmed || response.cancelled) return;
      if (isNew) {
        this._modalOverlay.close(ModalOverlayServiceCloseEventType.CLOSE);
      } else {
        await this._delete();
      }
    });
  }

  public endReached(event: number) {
    const current = this._page * this._itemsPerPage;

    if (current >= this._bannersData.length) return;

    this._page = this._page + 1;
    const next = this._page * this._itemsPerPage;
    const nextPage = this._bannersData.slice(current, next);

    this._imageList.next([...this._imageList.getValue(), ...nextPage]);
  }

  public statusChange(event: 'PENDING' | 'VALID') {
    if (event === 'PENDING') {
      this._formState.next('uploading');
    } else {
      this._formState.next('idle');
    }
  }

  public handleOnPersonSelected(person: Person) {
    const control = this.form.get(FtmActivityTemplatesEditFormFields.TEACHER);

    const value = person
      ? {
          name: person.displayName,
          hash: person.hash,
        }
      : null;

    control.setValue(value);
  }

  public markTeacherInputFieldAsTouched() {
    const control = this.form.get(FtmActivityTemplatesEditFormFields.TEACHER);
    control.markAsTouched();
  }

  public onSelectionChange(data: any) {
    const control = this.form.get(FtmActivityTemplatesEditFormFields.TEACHER);
    control.setValue({ name: '', hash: data });
  }

  public clickViewQrCode() {
    this._ftmAtService.openQrViewer(this._data).catch(error => {
      this._systemAlertSnackBar.error('There was an error generating QR code');
    });
  }

  private async _getBanners() {
    const banners = await this._ftmAtService.getBannerList();
    this._bannersData = banners;
    this._imageList.next(banners.slice(0, this._page * this._itemsPerPage));
  }

  private async _setInitialForm() {
    const existingId = (this._data as any)?.id;
    const isCopy = (this._data as any)?.copy;
    this._data = !existingId ? this._data : await this._ftmAtService.fetch(existingId);

    this._isNew.next(isCopy || !existingId);

    const type = this._data as FlexTimeActivity;

    if (type.name) {
      const name = isCopy ? `${type.name} Clone` : type.name;
      this.form.get(FtmActivityTemplatesEditFormFields.NAME).setValue(name);
    }

    if (type.description) {
      this.form.get(FtmActivityTemplatesEditFormFields.DESCRIPTION).setValue(type.description);
    }

    // if a teacher/staff is creating a new template, they will automatically be set as the main teacher
    if (this._isNew.value && !this._flexPermissions.isFlexTimeAdmin()) {
      this.form.get(FtmActivityTemplatesEditFormFields.TEACHER).setValue({
        name: `${this._authInfo.authPerson.firstName} ${this._authInfo.authPerson.lastName}`,
        hash: this._authInfo.authPerson.hash,
      });
    } else if (type.createdByPerson) {
      this.form.get(FtmActivityTemplatesEditFormFields.TEACHER).setValue({
        name: type.createdByPerson.name,
        hash: type.createdByPerson.hash,
      });
    }

    if (type.registrationLimit) {
      this.form.get(FtmActivityTemplatesEditFormFields.REGISTRATION_LIMIT).setValue(type.registrationLimit);
    }

    if (type.points) {
      this.form.get(FtmActivityTemplatesEditFormFields.POINTS).setValue(type.points);
    }

    if (type.bannerHash || type.imagePath) {
      this.form.get(FtmActivityTemplatesEditFormFields.BANNER).setValue({
        assetPath: type.imagePath || null,
        bannerHash: type.bannerHash || null,
      });
    }

    if (typeof type.allowStudentToRegister === 'boolean') {
      this.form.get(FtmActivityTemplatesEditFormFields.STUDENTS_TO_REGISTER).setValue(type.allowStudentToRegister);
    }

    if (type.restriction) {
      if (isCopy) {
        type.restrictionId = undefined;
        type.restriction.id = undefined;
        if (type.restriction.membershipListId) {
          // create a copy of the list here so we don't have to pass isCopy through more components
          const listOriginal = await this._listMembership.getMembershipList(type.restriction.membershipListId, true);
          const listCopy = await this._listMembership.updateMembershipList({ ...listOriginal, id: undefined });
          type.restriction.membershipListId = listCopy.id;
          type.restriction.membershipList = listCopy;
        }
      }
      this.form
        .get(FtmActivityTemplatesEditFormFields.RESTRICTION_MEMBERSHIP_LIST)
        .patchValue(toRestrictionInput(type.restriction));
    }

    if (type.location) {
      this.form.get(FtmActivityTemplatesEditFormFields.LOCATION).setValue(type.location);
    }

    if (type.type?.id) {
      this.form.get(FtmActivityTemplatesEditFormFields.ACTIVITY_TYPE).setValue(type.type.id);
    }

    if (type.teacherList) {
      if (isCopy) {
        // create a copy of the list here so we don't have to pass isCopy through more components
        const teacherList = {
          ...type.teacherList,
          id: undefined,
          members: Array.isArray(type.teacherList.members)
            ? type.teacherList.members.map(member => (typeof member === 'string' ? member : member.hash))
            : [],
        };
        const listCopy = await this._listMembership.updateMembershipList(teacherList);
        this._data.teacherListId = listCopy.id;
      }
      this._listSubject.next([{ id: this._data.teacherListId }]);
      this.form.get(FtmActivityTemplatesEditFormFields.TEACHER_LIST_ID).setValue(this._data.teacherListId);
    }

    if (type.enableSelfCheckin) {
      this.form.get(FtmActivityTemplatesEditFormFields.ENABLE_SELF_CHECKIN).setValue(type.enableSelfCheckin);
    }

    if (type.showStudentPhotoOnCheckin) {
      this.form
        .get(FtmActivityTemplatesEditFormFields.SHOW_STUDENT_PHOTO_ON_CHECKIN)
        .setValue(type.showStudentPhotoOnCheckin);
    }

    this._cdr.markForCheck();
  }

  private async _getActivityTypes() {
    const activityTypes = await this._ftmAtService.getFlexTimeActivityTypes();
    const activityTypeOptions = activityTypes.map(activityType => {
      return {
        label: activityType.name,
        value: activityType.id,
      } as FormSelectOption<number>;
    });
    this._activityTypeOptionsSubj.next(activityTypeOptions);
  }

  private async _create(activity) {
    try {
      await this._actionWithLoader(this._ftmAtService.upsertActivity(activity), 'creating');
      this._modalOverlay.close(ModalOverlayServiceCloseEventType.CREATE);
    } catch (e) {
      console.error(e);
      this._sentry.captureMessageAsError(FtmActivityTemplatesMessages.SNACKBAR_CREATE_ERROR, e);
      this._systemAlertSnackBar.open({
        type: 'error',
        message: FtmActivityTemplatesMessages.SNACKBAR_CREATE_ERROR,
      });
    }
  }

  private async _update(activity) {
    try {
      await this._actionWithLoader(this._ftmAtService.upsertActivity(activity), 'updating');
      this._modalOverlay.close(ModalOverlayServiceCloseEventType.SUBMIT);
    } catch (e) {
      this._sentry.captureMessageAsError(FtmActivityTemplatesMessages.SNACKBAR_UPDATE_ERROR, e);
      this._systemAlertSnackBar.open({
        type: 'error',
        message: FtmActivityTemplatesMessages.SNACKBAR_UPDATE_ERROR,
      });
    }
  }

  private async _delete() {
    try {
      await this._actionWithLoader(
        // lets just archive this so we dont create orphans on flex instances
        this._ftmAtService.archive((this._data as FlexTimeActivity)?.id),
        'deleting',
      );
      this._modalOverlay.close(ModalOverlayServiceCloseEventType.DELETE);
    } catch (e) {
      this._sentry.captureMessageAsError(FtmActivityTemplatesMessages.SNACKBAR_DELETE_ERROR, e);
      this._systemAlertSnackBar.open({
        type: 'error',
        message: e?.message || FtmActivityTemplatesMessages.SNACKBAR_DELETE_ERROR,
      });
    }
  }

  private async _actionWithLoader<T>(promise: Promise<T>, state: FormStateMachine) {
    try {
      this.form.disable();
      this._formState.next(state);
      await Promise.all([promise]);
    } catch (e) {
      throw e;
    } finally {
      this.form.enable();
      this._formState.next('idle');
    }
  }

  /**
   * We need to check and see if there's any future instances
   * associated with this template
   */
  private async _canActivityBeDeleted() {
    const id = (this._data as any)?.id;

    if (!id) return;

    const instances = await this._flexInstancesService.fetchAll({
      startDate: day(),
      endDate: day().add(1, 'year'),
      activityTemplateId: id,
    });

    const inUse = !isEmpty(instances);

    this._canDelete.next(!inUse);
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this._isNew.complete();
  }
}
